import { alpha } from "@mui/material/styles";
import { CSSProperties } from "@mui/material/styles/createMixins";

export const QR_FRAME_SELECTOR = ".ticket-qr-frame";
export const SCAN_ANIMATION_ROOT_CLASS = "zeffy-scan-animation";
export const SCAN_ANIMATION_FROZEN_CLASS = "zeffy-scan-animation--frozen";
export const SCAN_ANIMATION_STYLE_ID = "zeffy-scan-animation-style";
const SCAN_ANIMATION_LABEL_CLASS = "zeffy-scan-label";
const SCAN_ANIMATION_DURATION_SECONDS = 8;

const FIGMA_TO_TICKET_SCALE = 340 / 130.133;
const toTicketPixels = (figmaPixels: number) => `${(figmaPixels * FIGMA_TO_TICKET_SCALE).toFixed(2)}px`;

const QR_FRAME = { size: 130.133, radius: 8.95 };
const SCANNER_SIZE = 136;
const SCANNER_BLEED = (SCANNER_SIZE - QR_FRAME.size) / 2;
const BRACKET = { length: 43, thickness: 3, radius: 13 };
const BEAM_HEIGHT = 41;
const CHECK_SIZE = 43;
const LABEL = { fontSize: 14, lineHeight: 20, letterSpacing: 0.15, gap: 4 };

export interface ScanAnimationTheme {
  accentColor: string;
  overlayColor: string;
  labelColor: string;
  labelFontWeight: CSSProperties["fontWeight"] | undefined;
}

export interface ScanAnimationOptions {
  isFrozen: boolean;
  label: string;
  checkIconMarkup: string;
  theme: ScanAnimationTheme;
}

export const syncScanAnimation = (iframeDocument: Document, options: ScanAnimationOptions) => {
  const qrFrame = iframeDocument.querySelector<HTMLElement>(QR_FRAME_SELECTOR);
  if (!qrFrame) {
    return;
  }

  if (!iframeDocument.getElementById(SCAN_ANIMATION_STYLE_ID)) {
    const style = iframeDocument.createElement("style");
    style.id = SCAN_ANIMATION_STYLE_ID;
    style.textContent = buildScanAnimationStyles(options.theme);
    iframeDocument.head.appendChild(style);
  }

  const root =
    qrFrame.querySelector<HTMLElement>(`.${SCAN_ANIMATION_ROOT_CLASS}`) ??
    createScanAnimationRoot(iframeDocument, qrFrame, options.checkIconMarkup);

  root.classList.toggle(SCAN_ANIMATION_FROZEN_CLASS, options.isFrozen);
  const label = root.querySelector(`.${SCAN_ANIMATION_LABEL_CLASS}`);
  if (label) {
    label.textContent = options.label;
  }
};

const createScanAnimationRoot = (iframeDocument: Document, qrFrame: HTMLElement, checkIconMarkup: string) => {
  const root = iframeDocument.createElement("div");
  root.classList.add(SCAN_ANIMATION_ROOT_CLASS);
  root.setAttribute("aria-hidden", "true");
  root.setAttribute("data-test", "scan-animation");

  ["top-left", "top-right", "bottom-left", "bottom-right"].forEach((corner) => {
    const bracket = iframeDocument.createElement("span");
    bracket.classList.add("zeffy-scan-bracket", `zeffy-scan-bracket--${corner}`);
    root.appendChild(bracket);
  });

  const beam = iframeDocument.createElement("div");
  beam.classList.add("zeffy-scan-beam");
  root.appendChild(beam);

  const result = iframeDocument.createElement("div");
  result.classList.add("zeffy-scan-result");
  const check = iframeDocument.createElement("div");
  check.classList.add("zeffy-scan-check");
  check.innerHTML = checkIconMarkup;
  const label = iframeDocument.createElement("div");
  label.classList.add(SCAN_ANIMATION_LABEL_CLASS);
  result.append(check, label);
  root.appendChild(result);

  qrFrame.appendChild(root);
  return root;
};

export const buildScanAnimationStyles = (theme: ScanAnimationTheme) => {
  const root = `.${SCAN_ANIMATION_ROOT_CLASS}`;
  const animated = `${root}:not(.${SCAN_ANIMATION_FROZEN_CLASS})`;
  const bleed = toTicketPixels(SCANNER_BLEED);
  const bracketLength = toTicketPixels(BRACKET.length);
  const bracketBorder = `${toTicketPixels(BRACKET.thickness)} solid ${theme.accentColor}`;
  const bracketRadius = toTicketPixels(BRACKET.radius);
  const beamGradient = `linear-gradient(180deg, ${alpha(theme.accentColor, 0.026)} 0%, ${alpha(theme.accentColor, 0.8)} 68.27%, ${alpha(theme.accentColor, 0.95)} 75.96%, ${alpha(theme.accentColor, 0)} 100%)`;
  const beamTravel = toTicketPixels(SCANNER_SIZE - BEAM_HEIGHT);
  const duration = `${SCAN_ANIMATION_DURATION_SECONDS}s`;

  return `
    ${QR_FRAME_SELECTOR} { position: relative; }
    ${root} { position: absolute; inset: -${bleed}; pointer-events: none; }
    ${root} .zeffy-scan-bracket { position: absolute; z-index: 1; width: ${bracketLength}; height: ${bracketLength}; box-sizing: border-box; }
    ${root} .zeffy-scan-bracket--top-left { top: 0; left: 0; border-top: ${bracketBorder}; border-left: ${bracketBorder}; border-top-left-radius: ${bracketRadius}; }
    ${root} .zeffy-scan-bracket--top-right { top: 0; right: 0; border-top: ${bracketBorder}; border-right: ${bracketBorder}; border-top-right-radius: ${bracketRadius}; }
    ${root} .zeffy-scan-bracket--bottom-left { bottom: 0; left: 0; border-bottom: ${bracketBorder}; border-left: ${bracketBorder}; border-bottom-left-radius: ${bracketRadius}; }
    ${root} .zeffy-scan-bracket--bottom-right { bottom: 0; right: 0; border-bottom: ${bracketBorder}; border-right: ${bracketBorder}; border-bottom-right-radius: ${bracketRadius}; }
    ${root} .zeffy-scan-beam { position: absolute; top: 0; left: 0; right: 0; height: ${toTicketPixels(BEAM_HEIGHT)}; opacity: 0; background: ${beamGradient}; }
    ${root} .zeffy-scan-result { position: absolute; inset: ${bleed}; border-radius: ${toTicketPixels(QR_FRAME.radius)}; background: ${theme.overlayColor}; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: ${toTicketPixels(LABEL.gap)}; }
    ${root} .zeffy-scan-check { width: ${toTicketPixels(CHECK_SIZE)}; height: ${toTicketPixels(CHECK_SIZE)}; fill: ${theme.accentColor}; color: ${theme.accentColor}; }
    ${root} .zeffy-scan-check svg { width: 100%; height: 100%; }
    ${root} .${SCAN_ANIMATION_LABEL_CLASS} { font-family: Roboto, Helvetica, Arial, sans-serif; font-weight: ${theme.labelFontWeight}; font-size: ${toTicketPixels(LABEL.fontSize)}; line-height: ${toTicketPixels(LABEL.lineHeight)}; letter-spacing: ${toTicketPixels(LABEL.letterSpacing)}; color: ${theme.labelColor}; text-align: center; }
    ${animated} .zeffy-scan-bracket { animation: zeffy-scan-brackets ${duration} ease-in-out infinite; }
    ${animated} .zeffy-scan-beam { animation: zeffy-scan-beam ${duration} ease-in-out infinite; }
    ${animated} .zeffy-scan-result { animation: zeffy-scan-result ${duration} ease-out infinite; }
    ${animated} .zeffy-scan-check { animation: zeffy-scan-check ${duration} ease-out infinite; }
    @keyframes zeffy-scan-brackets { 0% { opacity: 0; } 6% { opacity: 1; } 90% { opacity: 1; } 100% { opacity: 0; } }
    @keyframes zeffy-scan-beam { 0%, 6% { opacity: 0; transform: translateY(0); } 9% { opacity: 1; transform: translateY(0); } 42% { opacity: 1; transform: translateY(${beamTravel}); } 46%, 100% { opacity: 0; transform: translateY(${beamTravel}); } }
    @keyframes zeffy-scan-result { 0%, 44% { opacity: 0; } 50%, 90% { opacity: 1; } 100% { opacity: 0; } }
    @keyframes zeffy-scan-check { 0%, 46% { transform: scale(0.6); } 52%, 100% { transform: scale(1); } }
  `;
};
